import { useEffect, useState } from 'react'
import { applyAccentTokens, DEFAULT_ACCENT } from '@/lib/theme'

export type Theme = 'light' | 'dark' | 'system'

const STORAGE_KEY = 'crm-theme'

function applyTheme(theme: Theme) {
  const root = document.documentElement
  root.classList.remove('light', 'dark')
  let isDark: boolean
  if (theme === 'system') {
    isDark = window.matchMedia('(prefers-color-scheme: dark)').matches
    root.classList.add(isDark ? 'dark' : 'light')
  } else {
    isDark = theme === 'dark'
    root.classList.add(theme)
  }
  applyAccentTokens(DEFAULT_ACCENT, isDark)
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(() => {
    try {
      return (localStorage.getItem(STORAGE_KEY) as Theme) ?? 'system'
    } catch {
      return 'system'
    }
  })

  useEffect(() => {
    applyTheme(theme)
  }, [theme])

  useEffect(() => {
    if (theme !== 'system') return
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = () => applyTheme('system')
    media.addEventListener('change', handler)
    return () => media.removeEventListener('change', handler)
  }, [theme])

  const setTheme = (next: Theme) => {
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // Storage unavailable (private mode / blocked) — theme simply won't persist
    }
    setThemeState(next)
  }

  return { theme, setTheme }
}
